/** SUNTRONIX ONE — Multi-Brand & Multi-Vertical Stock dashboards + masters. */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Layers, RefreshCw, Plus, Tags } from "lucide-react";
import { VtigerTable } from "@/components/app/VtigerTable";

const TABS = ["Stock by Brand", "Stock by Vertical", "Brand Master", "Vertical Master"];

const NUM = (k, label) => ({ key: k, label, num: true, align: "right", filter: false });
const STOCK_COLS = (label) => [
  { key: "key", label, render: (r) => <span className="font-semibold text-slate-800">{(r.key || "UNCLASSIFIED").replace(/_/g, " ")}</span> },
  NUM("on_hand", "On hand"), NUM("available", "Available"), NUM("reserved", "Reserved"),
  NUM("in_transit", "In transit"), NUM("repair", "Repair"), NUM("quarantine", "Quarantine"),
  NUM("customer_owned", "Customer-owned"),
];

const Tile = ({ label, value, tone = "text-slate-900" }) => (
  <div className="bg-white border border-slate-200 rounded-md px-4 py-3">
    <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
    <div className={`text-2xl font-bold ${tone}`}>{value}</div>
  </div>
);

function AddBrandModal({ verticals, onClose, onSaved }) {
  const [f, setF] = useState({ name: "", country: "", website: "", verticals: [] });
  const save = async () => {
    if (!f.name.trim()) return toast.error("Brand name required");
    try {
      await api.post("/mb-inventory/brands", f);
      toast.success("Brand created");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center" data-testid="add-brand-modal">
      <div className="bg-white rounded-lg p-5 w-[420px] space-y-3">
        <h3 className="font-semibold text-slate-800">Add Brand</h3>
        <input className="w-full h-9 px-2 border border-slate-300 rounded text-sm" placeholder="Brand name *"
          value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="brand-name-input" />
        <input className="w-full h-9 px-2 border border-slate-300 rounded text-sm" placeholder="Country"
          value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} />
        <input className="w-full h-9 px-2 border border-slate-300 rounded text-sm" placeholder="Website"
          value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} />
        <div className="flex flex-wrap gap-1.5">
          {verticals.map((v) => (
            <button key={v.name} onClick={() => setF((s) => ({ ...s,
              verticals: s.verticals.includes(v.name) ? s.verticals.filter((x) => x !== v.name) : [...s.verticals, v.name] }))}
              className={`text-[10px] px-2 py-1 rounded-full border ${f.verticals.includes(v.name)
                ? "bg-emerald-700 text-white border-emerald-700" : "border-slate-300 text-slate-600"}`}>
              {v.display_name}
            </button>))}
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="h-9 px-3 text-sm border border-slate-300 rounded" data-testid="brand-cancel-btn">Cancel</button>
          <button onClick={save} className="h-9 px-4 text-sm bg-emerald-700 text-white rounded" data-testid="brand-save-btn">Save Brand</button>
        </div>
      </div>
    </div>
  );
}

export default function MultiBrandStock() {
  const [tab, setTab] = useState(TABS[0]);
  const [byBrand, setByBrand] = useState([]);
  const [byVertical, setByVertical] = useState([]);
  const [brands, setBrands] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [modal, setModal] = useState(false);
  const [newVertical, setNewVertical] = useState("");

  const load = useCallback(() => Promise.all([
    api.get("/mb-inventory/stock-by-brand").then(({ data }) => setByBrand(data.items || [])),
    api.get("/mb-inventory/stock-by-vertical").then(({ data }) => setByVertical(data.items || [])),
    api.get("/mb-inventory/brands").then(({ data }) => setBrands(data.items || [])),
    api.get("/mb-inventory/verticals").then(({ data }) => setVerticals(data.items || [])),
  ]).catch(() => toast.error("Failed to load stock data")), []);
  useEffect(() => { load(); }, [load]);

  const addVertical = async () => {
    if (!newVertical.trim()) return;
    try {
      await api.post("/mb-inventory/verticals", { name: newVertical });
      toast.success("Vertical created"); setNewVertical(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const totalOnHand = byBrand.reduce((s, r) => s + (r.on_hand || 0), 0);
  const totalAvail = byBrand.reduce((s, r) => s + (r.available || 0), 0);

  return (
    <div className="max-w-[1500px] mx-auto space-y-4" data-testid="multibrand-stock-page">
      <header className="flex items-center gap-3 flex-wrap">
        <div className="h-10 w-10 rounded-lg bg-emerald-900 text-white grid place-items-center"><Layers className="h-5 w-5" /></div>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-slate-900">Multi-Brand Stock</h1>
          <p className="text-xs text-slate-500">One inventory engine across every brand and technology vertical</p>
        </div>
        <button onClick={load} className="h-9 px-3 text-sm border border-slate-300 rounded inline-flex items-center gap-1.5" data-testid="mb-refresh-btn">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh</button>
      </header>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Tile label="Units on hand" value={totalOnHand} />
        <Tile label="Available" value={totalAvail} tone="text-emerald-700" />
        <Tile label="Brands" value={brands.length} />
        <Tile label="Verticals" value={verticals.filter((v) => v.status === "ACTIVE").length} />
      </div>

      <div className="flex gap-1.5 flex-wrap">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`mb-tab-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={`h-8 px-3 text-xs font-medium rounded-full border ${tab === t
              ? "bg-emerald-900 text-white border-emerald-900" : "bg-white text-slate-600 border-slate-300"}`}>{t}</button>))}
      </div>

      {tab === "Stock by Brand" && <VtigerTable columns={STOCK_COLS("Brand")} rows={byBrand.map((r) => ({ ...r, id: r.key }))} testid="stock-by-brand-table" viewKey="stock-by-brand" />}
      {tab === "Stock by Vertical" && (
        <div className="space-y-2">
          {byVertical.every((r) => (r.key || "").toUpperCase() === "UNCLASSIFIED") && (
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="vertical-hint-banner">
              Stock is not yet classified by vertical. Assign verticals to products in{" "}
              <a href="/admin/product-classification" className="font-semibold underline">Product Classification</a> to unlock this view.
            </div>)}
          <VtigerTable columns={STOCK_COLS("Vertical")} rows={byVertical.map((r) => ({ ...r, id: r.key }))} testid="stock-by-vertical-table" />
        </div>)}

      {tab === "Brand Master" && (
        <div className="space-y-3">
          <button onClick={() => setModal(true)} className="h-9 px-3 text-sm bg-emerald-700 text-white rounded inline-flex items-center gap-1.5" data-testid="add-brand-btn">
            <Plus className="h-3.5 w-3.5" /> Add Brand</button>
          <VtigerTable testid="brand-master-table" rows={brands}
            columns={[
              { key: "name", label: "Brand", cls: "font-semibold" },
              { key: "verticals", label: "Verticals", value: (r) => (r.verticals || []).join(", ") },
              { key: "country", label: "Country" },
              { key: "website", label: "Website" },
              { key: "status", label: "Status", value: (r) => r.status || (r.active === false ? "INACTIVE" : "ACTIVE"),
                render: (r) => { const st = r.status || (r.active === false ? "INACTIVE" : "ACTIVE");
                  return <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${st === "ACTIVE" ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600"}`}>{st}</span>; } },
            ]} />
        </div>)}

      {tab === "Vertical Master" && (
        <div className="space-y-3">
          <div className="flex gap-2 items-center">
            <Tags className="h-4 w-4 text-slate-400" />
            <input value={newVertical} onChange={(e) => setNewVertical(e.target.value)}
              placeholder="New vertical name" data-testid="new-vertical-input"
              className="h-9 px-2 border border-slate-300 rounded text-sm w-64" />
            <button onClick={addVertical} className="h-9 px-3 text-sm bg-emerald-700 text-white rounded" data-testid="add-vertical-btn">Add Vertical</button>
          </div>
          <VtigerTable testid="vertical-master-table" rows={verticals}
            columns={[
              { key: "display_name", label: "Vertical", cls: "font-semibold" },
              { key: "name", label: "Code", mono: true },
              { key: "status", label: "Status" },
            ]} />
        </div>)}

      {modal && <AddBrandModal verticals={verticals} onClose={() => setModal(false)}
        onSaved={() => { setModal(false); load(); }} />}
    </div>
  );
}
